import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Flame, Rocket } from "lucide-react";
import { api } from "@/lib/apiClient";
import { JOURNEY_STAGES } from "@/constants/ingredients";
import PopsicleSVG from "@/components/PopsicleSVG";

const STATUSES = ["pending", "shortlisted", "rejected", "made"];

export default function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/admin/dream-pops").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/admin/dream-pops/${id}/status`, { status });
    toast.success(`Set ${status}`); load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/dream-pops/${id}`); load(); };

  const filtered = filter === "all" ? items : items.filter((i) => (i.status === filter || i.journey_stage === filter));

  return (
    <div data-testid="admin-submissions" className="space-y-8">
      <header className="flex items-end justify-between flex-col lg:flex-row gap-4">
        <div><div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Inventions</div><h1 className="editorial-h1 text-5xl mt-2">{items.length} community recipes.</h1></div>
      </header>

      <div className="flex flex-wrap gap-2">
        {["all", ...STATUSES, ...JOURNEY_STAGES.map((j) => j.key)].map((s) => (
          <button key={s} data-testid={`subm-filter-${s}`} onClick={() => setFilter(s)} className={`btn-pill text-xs ${filter === s ? "btn-dark" : "btn-ghost border border-brand-line"}`}>{s.replace(/_/g, " ")}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {filtered.map((s) => {
          const total = Object.values(s.votes || {}).reduce((a, b) => a + b, 0);
          return (
            <article key={s.id} className="card-elevated p-5" data-testid={`subm-${s.id}`}>
              <div className="grid sm:grid-cols-5 gap-4">
                <div className="sm:col-span-2 aspect-square rounded-2xl bg-brand-muted grid place-items-center relative overflow-hidden">
                  <div className="absolute inset-4 rounded-full" style={{ background: `radial-gradient(circle, ${s.color_primary}33, transparent 70%)` }} />
                  <PopsicleSVG primary={s.color_primary} secondary={s.color_secondary} shape={s.shape} className="h-3/4" animate={false} />
                </div>
                <div className="sm:col-span-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-2xl">{s.pop_name}</div>
                      {s.tagline && <div className="text-xs italic text-brand-charcoal/65 mt-0.5">"{s.tagline}"</div>}
                      <div className="text-xs text-brand-charcoal/60 mt-1">{s.customer_name} · {s.customer_phone}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.18em] border border-brand-line rounded-full px-2 py-0.5 shrink-0">{s.status}</span>
                  </div>
                  {s.ingredients?.length > 0 && (
                    <div className="mt-3 flex h-2 rounded-full overflow-hidden border border-brand-line">
                      {s.ingredients.map((i) => (<div key={i.name} style={{ width: `${i.percent}%`, background: "#FFC93C" }} title={`${i.name} ${i.percent}%`} />))}
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-brand-charcoal/70">
                    <div className="flex items-center gap-1"><Heart size={12} className="text-brand-red"/> {s.votes?.love || 0}</div>
                    <div className="flex items-center gap-1"><Flame size={12} style={{color:"#FFC93C"}}/> {s.votes?.must_try || 0}</div>
                    <div className="flex items-center gap-1"><Rocket size={12}/> {s.votes?.make_now || 0}</div>
                  </div>
                </div>
              </div>

              {/* Journey progress */}
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55 mb-2">Journey</div>
                <div className="flex flex-wrap gap-1">
                  {JOURNEY_STAGES.map((j) => (
                    <button key={j.key} onClick={() => setStatus(s.id, j.key)} className={`text-[10px] tracking-[0.05em] rounded-full px-2 py-0.5 border ${s.journey_stage === j.key ? "border-brand-charcoal bg-brand-yellow/30" : "border-brand-line text-brand-charcoal/70"}`}>{j.label}</button>
                  ))}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-1 items-center">
                <span className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55 mr-1">Status:</span>
                {STATUSES.map((st) => (<button key={st} onClick={() => setStatus(s.id, st)} className="btn-pill btn-ghost text-[10px] border border-brand-line">{st}</button>))}
                <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="btn-pill btn-ghost text-[10px] border border-brand-line">{expanded === s.id ? "Hide" : "Details"}</button>
                <button onClick={() => del(s.id)} className="btn-pill btn-ghost text-[10px] text-brand-red border border-brand-line">delete</button>
              </div>

              {expanded === s.id && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs text-brand-charcoal/80">
                  <div><div className="uppercase tracking-[0.18em] text-brand-charcoal/55 text-[10px]">Taste</div><div>{s.taste_profile || "—"}</div></div>
                  <div><div className="uppercase tracking-[0.18em] text-brand-charcoal/55 text-[10px]">Audience</div><div>{s.target_audience || "—"}</div></div>
                  <div><div className="uppercase tracking-[0.18em] text-brand-charcoal/55 text-[10px]">Unique</div><div>{s.unique_features || "—"}</div></div>
                  <div><div className="uppercase tracking-[0.18em] text-brand-charcoal/55 text-[10px]">Why produce</div><div>{s.why_produce || "—"}</div></div>
                  <div className="sm:col-span-2"><div className="uppercase tracking-[0.18em] text-brand-charcoal/55 text-[10px]">Description</div><div>{s.description || s.notes || "—"}</div></div>
                  {s.inspiration_url && (<div className="sm:col-span-2"><a href={s.inspiration_url} target="_blank" rel="noreferrer" className="text-brand-red underline">View inspiration ↗</a></div>)}
                  {s.ingredients?.length > 0 && (
                    <div className="sm:col-span-2">
                      <div className="uppercase tracking-[0.18em] text-brand-charcoal/55 text-[10px] mb-1">Recipe</div>
                      <ul className="space-y-1">{s.ingredients.map((i) => (<li key={i.name} className="flex justify-between border-b border-brand-line py-0.5"><span>{i.name}</span><span className="font-medium">{i.percent}%</span></li>))}</ul>
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
        {filtered.length === 0 && <div className="text-brand-charcoal/60">No submissions match.</div>}
      </div>
    </div>
  );
}
